import { ConvertToMerchantList, GetSelectedMerchant } from "@/helper/merchantHelper";
import {
  getReferenceOtpCookie,
  isReferenceOtpValid,
  removeReferenceOtpCookie,
  setReferenceOtpCookie,
} from "@/helper/twoFactorAuthHelper";
import { MerchantInFoModel } from "@/model/merchant/MerchantInFoModel";
import { SelectedMerchantModel } from "@/model/merchant/selectedMerchantModel";
import { UserModel } from "@/model/user";
import router from "@/router";
import API from "@/service/index";
import { ConvertToUserModel } from "@/service/user/authenFacade";
import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";
import { ActivityLogSource, AuthenticationResponseCode, LoadingStatusType } from "../../enums/enums";

@Module({ namespaced: true })
export default class UserInfo extends VuexModule {
  public loginSuccess: LoadingStatusType = LoadingStatusType.None;
  public userModel = {} as UserModel;
  public merchantList = [] as Array<MerchantInFoModel>;
  public merchantListLoadingStatus: LoadingStatusType = LoadingStatusType.None;
  public selectMerchant: SelectedMerchantModel | null = null;
  public setSelectMerchantLoadingStatus: LoadingStatusType = LoadingStatusType.None;
  public merchantModelByIdLoadingStatus: LoadingStatusType = LoadingStatusType.None;
  public merchantModelListForMap: Map<number, SelectedMerchantModel> = new Map();
  public alertMessage = "";

  // 2FA properties
  public twoFactorAuthData: any = null;
  public originalLoginRequest: any = null;
  public twoFactorErrorMessage = "";
  public verifyNextDelay = 0;
  public loginResponseData: any = null;

  public loginGuardData: any = null;
  public loginLockedData: any = null;
  public needRecaptchaV2 = false;

  get getLoginGuardData() {
    return this.loginGuardData;
  }

  get getLoginLockedData() {
    return this.loginLockedData;
  }

  get getNeedRecaptchaV2() {
    return this.needRecaptchaV2;
  }

  @Mutation
  public setNeedRecaptchaV2(value: boolean): void {
    this.needRecaptchaV2 = value;
  }
  @Mutation
  public setLogInSuccess(userModel: any): void {
    this.userModel = userModel;
    this.loginSuccess = LoadingStatusType.Success;
  }

  @Mutation
  public setLoginFail2FA(message: string): void {
    this.loginSuccess = LoadingStatusType.Fail;
    this.alertMessage = message;
  }

  @Mutation
  public setLoginFail(): void {
    this.loginSuccess = LoadingStatusType.Fail;
    this.alertMessage = "";
  }

  @Mutation
  public setLoginLoading(): void {
    this.loginSuccess = LoadingStatusType.Loading;
  }

  @Mutation
  public MutationSetMerchantsList(merchantsList: Array<MerchantInFoModel>) {
    this.merchantList = merchantsList;
  }

  @Mutation
  public MutationSetMerchantListLoadingStatus(status: LoadingStatusType) {
    this.merchantListLoadingStatus = status;
  }

  @Mutation
  public MutationsSelectMerchant(_object: SelectedMerchantModel): void {
    this.selectMerchant = _object;
  }

  @Mutation
  public MutationsSetSelectMerchantLoadingStatus(status: LoadingStatusType): void {
    this.setSelectMerchantLoadingStatus = status;
  }

  @Mutation
  public MutationSetMerchantModelByIdLoadingStatus(status: LoadingStatusType) {
    this.merchantModelByIdLoadingStatus = status;
  }

  @Mutation
  MutationSetMerchantModelList(merchantModel: SelectedMerchantModel) {
    this.merchantModelListForMap.set(merchantModel.merchantId, merchantModel);
  }

  // 2FA Mutations
  @Mutation
  public setTwoFactorAuthData(data: any): void {
    this.twoFactorAuthData = data;
  }

  @Mutation
  public setOriginalLoginRequest(request: any): void {
    this.originalLoginRequest = request;
  }

  @Mutation
  public setTwoFactorErrorMessage(message: string): void {
    this.twoFactorErrorMessage = message;
  }

  @Mutation
  public setVerifyNextDelay(delay: number): void {
    this.verifyNextDelay = delay;
  }

  @Mutation
  public setLoginResponseData(data: any): void {
    this.loginResponseData = data;
  }

  @Mutation
  public setLoginGuardData(data: any): void {
    this.loginGuardData = data;
  }

  @Mutation
  public setLoginLockedData(data: any): void {
    this.loginLockedData = data;
  }

  @Mutation
  public clearTwoFactorErrorMessage(): void {
    this.twoFactorErrorMessage = "";
  }

  @Action
  public DoLogIn(logInData: any): void {
    this.context.commit("setLoginLoading");
    this.context.commit("setLoginGuardData", null);
    this.context.commit("setLoginLockedData", null);
    this.context.commit("setNeedRecaptchaV2", false);

    const recaptchaToken = logInData.recaptchaToken || "";
    const recaptchaVersion = logInData.recaptchaVersion || "";

    // เก็บข้อมูล login request ต้นฉบับ
    const originalRequest = {
      email: logInData.email,
      encpw: logInData.encpw,
      userType: 0,
      recaptchaToken,
      recaptchaVersion,
    };
    this.context.commit("setOriginalLoginRequest", originalRequest);

    const source = ActivityLogSource.LineConnect;

    API.AuthenService.DoGetAccessToken({
      userName: logInData.email,
      password: logInData.encpw,
      userType: 0,
      source,
      recaptchaToken,
      recaptchaVersion,
    })
      .then(async (res: any) => {
        // ตรวจสอบ response_code ก่อน เพื่อจัดการ 2FA
        const responseCode = res.data.response_code ? res.data.response_code : 200;

        const httpStatus = res.status || 0;
        const isAccountLocked =
          httpStatus === AuthenticationResponseCode.AccountLocked ||
          Number(res.data.response_code) === AuthenticationResponseCode.AccountLocked;
        if (isAccountLocked) {
          this.context.commit("setLoginLockedData", { lockedUntil: res.data.locked_until || "" });
          this.context.commit("setTwoFactorAuthData", null);
          this.context.commit("setLoginFail");
          return;
        }

        if (responseCode === AuthenticationResponseCode.RequireEmailOTP) {
          // ตรวจสอบว่าเป็น invalid_otp หรือไม่
          if (res.data.invalid_otp === true) {
            this.context.commit("setLoginLoading");
            return; // ไม่ทำอะไรเพิ่มเติม
          }

          // เก็บ reference_otp ใน cookie เมื่อได้ response_code 201
          if (res.data.reference_otp && res.data.reference_otp_live) {
            setReferenceOtpCookie(logInData.email, res.data.reference_otp, res.data.reference_otp_live);
          }

          // เก็บข้อมูลสำหรับ 2FA
          const twoFactorData = {
            type: 1, // Email OTP
            referenceOtp: res.data.reference_otp || "",
            recoveryCodeAttempt: res.data.recovery_code_attempt || 0,
          };
          this.context.commit("setTwoFactorAuthData", twoFactorData);
          // Reset loading status เพื่อให้ 2FA modal สามารถทำงานได้
          this.context.commit("setLoginFail2FA", "");
        } else if (responseCode === AuthenticationResponseCode.RequireAuthenticatorOTP) {
          // RequireAuthenticatorOTP
          const twoFactorData = {
            type: 2, // Authenticator OTP
            referenceOtp: res.data.reference_otp || "",
            twoFactorSecretKey: res.data.two_factor_secret_key || "",
            recoveryCodeAttempt: res.data.recovery_code_attempt || 0,
          };
          this.context.commit("setTwoFactorAuthData", twoFactorData);
          // Reset loading status เพื่อให้ 2FA modal สามารถทำงานได้
          this.context.commit("setLoginFail2FA", "");
        } else if (responseCode === AuthenticationResponseCode.EmailOTPAlreadyRequested) {
          // EmailOTPAlreadyRequested
          // ตรวจสอบ cookie สำหรับ reference_otp เมื่อได้ response_code 303
          let referenceOtp = res.data.reference_otp;
          if (!referenceOtp || referenceOtp === null) {
            const cookieData = getReferenceOtpCookie(logInData.email);
            if (cookieData && isReferenceOtpValid(logInData.email)) {
              referenceOtp = cookieData.referenceOtp;
            }
          }

          // เก็บข้อมูลสำหรับ 2FA
          const twoFactorData = {
            type: 1, // Email OTP
            referenceOtp: referenceOtp || "",
            recoveryCodeAttempt: res.data.recovery_code_attempt || 0,
          };
          this.context.commit("setTwoFactorAuthData", twoFactorData);
          // Reset loading status เพื่อให้ 2FA modal สามารถทำงานได้
          this.context.commit("setLoginFail2FA", "");
        } else if (responseCode === 200) {
          // Login สำเร็จ
          const token = {
            accessToken: res.data.access_token,
            refreshToken: res.data.refresh_token,
          };
          sessionStorage.setItem("access_token", token.accessToken);
          sessionStorage.setItem("refresh_token", token.refreshToken);
          this.context.dispatch("DoGetUserInfo");
        } else if (
          responseCode === AuthenticationResponseCode.RequireRecaptchaV2 ||
          responseCode === AuthenticationResponseCode.RecaptchaLowScore
        ) {
          // Backend ต้องการให้ user ทำ reCAPTCHA V2 challenge
          this.context.commit("setNeedRecaptchaV2", true);
          this.context.commit("setLoginFail2FA", "");
        } else {
          // Login ล้มเหลว
          if (res.data.login_guard) {
            this.context.commit("setLoginGuardData", res.data.login_guard);
          }
          this.context.commit("setLoginFail");
        }
      })
      .catch(() => {
        this.context.commit("setLoginFail");
      });
  }
  @Action
  public DoGetUserInfo(): void {
    API.AuthenService.DoGetUserInfo().then((resUserInfo: any) => {
      if (resUserInfo.status.IsSuccess()) {
        const userModel = ConvertToUserModel(resUserInfo.data.userInfo);
        this.context.commit("setLogInSuccess", userModel);
      } else {
        this.context.commit("setLoginFail");
        router.push("/error");
      }
    });
  }

  @Action
  public DoGetMerchantList() {
    if (this.merchantListLoadingStatus !== LoadingStatusType.Loading) {
      this.context.commit("MutationSetMerchantListLoadingStatus", LoadingStatusType.Loading);
      API.AuthenService.DoGetMerchantListByUserId().then((res) => {
        if (res.status.IsSuccess() && res.data.responseCode.IsSuccess()) {
          const merchantList = ConvertToMerchantList(res.data.merchantList);
          this.context.commit("MutationSetMerchantsList", merchantList);
        }
        this.context.commit("MutationSetMerchantListLoadingStatus", LoadingStatusType.Success);
      });
    }
  }

  @Action
  public DoSelectMerchant(merchantId: number) {
    if (this.setSelectMerchantLoadingStatus !== LoadingStatusType.Loading) {
      this.context.commit("MutationsSetSelectMerchantLoadingStatus", LoadingStatusType.Loading);
      API.AuthenService.DoGetSelectedMerchant(merchantId).then((res) => {
        if (res.status.IsSuccess() && res.data.responseCode.IsSuccess()) {
          const selectMerchant = GetSelectedMerchant(res.data.merchant);
          this.context.commit("MutationsSelectMerchant", selectMerchant);
        } else {
          this.context.commit("MutationsSelectMerchant", null);
        }
        this.context.commit("MutationsSetSelectMerchantLoadingStatus", LoadingStatusType.Success);
      });
    }
  }

  @Action
  DoGetMerchantModelById(merchantId: number) {
    return new Promise((resolve) => {
      this.context.commit("MutationSetMerchantModelByIdLoadingStatus", LoadingStatusType.Loading);

      const merchantModelOnLocal = this.merchantModelListForMap.get(merchantId) ?? null;

      if (merchantModelOnLocal) {
        setTimeout(() => {
          this.context.commit("MutationSetMerchantModelByIdLoadingStatus", LoadingStatusType.Success);
          resolve(merchantModelOnLocal);
        }, 0);
      } else {
        API.AuthenService.DoGetSelectedMerchant(merchantId).then((res: any) => {
          if (res.status.IsSuccess() || res.data.responseCode.IsSuccess()) {
            const merchant = GetSelectedMerchant(res.data.merchant);
            this.context.commit("MutationSetMerchantModelList", merchant);
            this.context.commit("MutationSetMerchantModelByIdLoadingStatus", LoadingStatusType.Success);
            resolve(merchant);
          }
        });
      }
    });
  }

  get getLogInSuccess(): LoadingStatusType {
    return this.loginSuccess;
  }
  get getMerchantList(): Array<MerchantInFoModel> {
    return this.merchantList;
  }
  get getMerchantListLoadingStatus(): LoadingStatusType {
    return this.merchantListLoadingStatus;
  }
  get getUserModel(): UserModel {
    return this.userModel;
  }
  get getMerchant(): SelectedMerchantModel | null {
    return this.selectMerchant;
  }
  get getSetSelectMerchantLoadingStatus(): any {
    return this.setSelectMerchantLoadingStatus;
  }
  get getMerchantModelListForMap() {
    return this.merchantModelListForMap;
  }

  get getMerchantModelByIdLoadingStatus() {
    return this.merchantModelByIdLoadingStatus;
  }
  get getAlertMessage(): string {
    return this.alertMessage;
  }

  // 2FA Getters
  get getTwoFactorAuthData(): any {
    return this.twoFactorAuthData;
  }

  get getOriginalLoginRequest(): any {
    return this.originalLoginRequest;
  }

  get getTwoFactorErrorMessage(): string {
    return this.twoFactorErrorMessage;
  }

  get getVerifyNextDelay(): number {
    return this.verifyNextDelay;
  }

  get getLoginResponseData(): any {
    return this.loginResponseData;
  }

  // 2FA Actions
  @Action
  public SetTwoFactorAuthData(data: any): void {
    this.context.commit("setTwoFactorAuthData", data);
  }

  @Action
  public ClearTwoFactorErrorMessage(): void {
    this.context.commit("clearTwoFactorErrorMessage");
  }

  @Action
  public SetVerifyNextDelay(delay: number): void {
    this.context.commit("setVerifyNextDelay", delay);
  }

  @Action
  public async verifySignInOTP({ otp, referenceOtp, twoFactorSecretKey, recoveryCode }: any): Promise<void> {
    return new Promise((resolve) => {
      this.context.commit("setLoginLoading");

      // ใช้ข้อมูล login request ต้นฉบับ + เพิ่ม otp และ reference_otp
      const originalRequest = this.originalLoginRequest;

      const source = ActivityLogSource.LineConnect;
      API.AuthenService.DoGetAccessToken({
        userName: originalRequest.email,
        password: originalRequest.encpw,
        userType: originalRequest.userType || 0,
        source,
        otp: recoveryCode ? "" : otp,
        referenceOtp: recoveryCode ? "" : referenceOtp,
        regenerateOtp: false,
        twoFactorSecretKey: recoveryCode ? "" : twoFactorSecretKey,
        recoveryCode: recoveryCode || "",
        recaptchaToken: originalRequest.recaptchaToken || "",
        recaptchaVersion: originalRequest.recaptchaVersion || "",
      })
        .then((response: any) => {
          // ตรวจสอบ response data - อาจจะเป็น error response (ไม่มี response.data แต่มี response โดยตรง)
          const responseData = response.data || response;
          
          // เก็บ loginResponseData สำหรับ recovery code flow
          this.context.commit("setLoginResponseData", responseData);

          // ตรวจสอบ invalid_recovery_code ก่อน
          if (responseData && responseData.invalid_recovery_code === true) {
            const remaining = responseData.recovery_code_attempt !== undefined 
              ? 10 - responseData.recovery_code_attempt 
              : 10;
            const errorMessage = `รหัสไม่ถูกต้อง กรุณากรอกรหัสที่ถูกต้องอีกครั้ง (เหลือ ${remaining} ครั้ง)`;
            this.context.commit("setTwoFactorErrorMessage", errorMessage);

            // อัปเดต recoveryCodeAttempt ใน twoFactorAuthData
            if (responseData.recovery_code_attempt !== undefined) {
              const currentTwoFactorData = this.twoFactorAuthData || {};
              this.context.commit("setTwoFactorAuthData", {
                ...currentTwoFactorData,
                recoveryCodeAttempt: responseData.recovery_code_attempt,
              });
            }

            // เก็บ verify_next_delay สำหรับการนับถอยหลัง
            if (responseData.verify_next_delay && responseData.verify_next_delay > 0) {
              this.context.commit("setVerifyNextDelay", responseData.verify_next_delay);
            }
            this.context.commit("setLoginFail2FA", errorMessage);
            resolve();
            return;
          }

          // ตรวจสอบ invalid_otp และ responseCode ก่อน
          if (responseData && responseData.invalid_otp === true) {
            let errorMessage = "";

            // ตรวจสอบ responseCode เพื่อแยกประเภท error
            const responseCode = responseData.response_code
              ? responseData.response_code
              : AuthenticationResponseCode.OK;

            if (responseCode === AuthenticationResponseCode.EmailOTPExpired) {
              // OTP หมดอายุ
              errorMessage = "รหัส OTP หมดอายุกรุณาส่งใหม่อีกครั้ง";
            } else {
              // OTP ผิด
              errorMessage = "รหัสไม่ถูกต้อง กรุณากรอกรหัสที่ถูกต้องอีกครั้ง";
            }

            // แสดง error message และไม่ปิด modal
            this.context.commit("setTwoFactorErrorMessage", errorMessage);

            // เก็บ verify_next_delay สำหรับการนับถอยหลัง
            if (responseData.verify_next_delay && responseData.verify_next_delay > 0) {
              this.context.commit("setVerifyNextDelay", responseData.verify_next_delay);
            }
            // Reset loading status เพื่อให้สามารถใส่ OTP ใหม่ได้
            this.context.commit("setLoginFail2FA", errorMessage);
            resolve();
            return;
          }

          const responseCode = responseData.response_code
            ? responseData.response_code
            : AuthenticationResponseCode.OK;

          // ตรวจสอบ EmailOTPAlreadyRequested (303) - อัปเดต recoveryCodeAttempt เมื่อได้ response ใหม่
          if (responseCode === AuthenticationResponseCode.EmailOTPAlreadyRequested) {
            // อัปเดต recoveryCodeAttempt จาก response
            if (responseData.recovery_code_attempt !== undefined) {
              const currentTwoFactorData = this.twoFactorAuthData || {};
              this.context.commit("setTwoFactorAuthData", {
                ...currentTwoFactorData,
                recoveryCodeAttempt: responseData.recovery_code_attempt,
              });
            }
            // เก็บ loginResponseData เพื่อให้ modal ตรวจสอบได้
            this.context.commit("setLoginResponseData", responseData);
            this.context.commit("setLoginFail2FA", "");
            resolve();
            return;
          }

          // ตรวจสอบ DisabledTwoFactor (305) - ทั้งกรณี disabled_twofactor: true และ false
          if (responseCode === AuthenticationResponseCode.DisabledTwoFactor && recoveryCode) {
            // เก็บข้อมูล response สำหรับตรวจสอบ disabled_twofactor
            this.context.commit("setLoginResponseData", responseData);

            if (responseData.disabled_twofactor === true) {
              // disabled_twofactor: true - แสดง confirmation step
              this.context.commit("setLoginFail2FA", "");
              resolve();
              return;
            } else {
              // disabled_twofactor: false - recovery code ผิด แสดง error ใน modal
              const attemptCount = responseData.recovery_code_attempt || 0;
              const remaining = 10 - attemptCount;
              const errorMessage = `รหัสไม่ถูกต้อง กรุณากรอกรหัสที่ถูกต้องอีกครั้ง (เหลือ ${remaining} ครั้ง)`;
              this.context.commit("setTwoFactorErrorMessage", errorMessage);
              
              // อัปเดต recoveryCodeAttempt ใน twoFactorAuthData
              if (responseData.recovery_code_attempt !== undefined) {
                const currentTwoFactorData = this.twoFactorAuthData || {};
                this.context.commit("setTwoFactorAuthData", {
                  ...currentTwoFactorData,
                  recoveryCodeAttempt: responseData.recovery_code_attempt,
                });
              }

              // เก็บ verify_next_delay สำหรับการนับถอยหลัง
              if (responseData.verify_next_delay && responseData.verify_next_delay > 0) {
                this.context.commit("setVerifyNextDelay", responseData.verify_next_delay);
              }
              
              this.context.commit("setLoginFail2FA", errorMessage);
              resolve();
              return;
            }
          }

          if (responseData && responseData.access_token && (response.status?.IsSuccess?.() || response.status === undefined)) {
            const token = {
              accessToken: responseData.access_token,
              refreshToken: responseData.refresh_token,
            };
            sessionStorage.setItem("access_token", token.accessToken);
            sessionStorage.setItem("refresh_token", token.refreshToken);

            // ลบ reference_otp cookie เมื่อ login สำเร็จ
            if (originalRequest.email) {
              removeReferenceOtpCookie(originalRequest.email);
            }

            this.context.dispatch("DoGetUserInfo");
          } else {
            // Reset loading status เพื่อให้สามารถใส่ OTP ใหม่ได้
            this.context.commit("setLoginFail2FA", "รหัสไม่ถูกต้อง กรุณากรอกรหัสที่ถูกต้องอีกครั้ง");
          }
        })
        .catch((error) => {
          console.error("Verify OTP Error:", error);
          // Reset loading status เพื่อให้สามารถใส่ OTP ใหม่ได้
          this.context.commit("setLoginFail2FA", "รหัสไม่ถูกต้อง กรุณากรอกรหัสที่ถูกต้องอีกครั้ง");
        });

      resolve();
    });
  }

  @Action
  public async regenerateSignInOTP(): Promise<void> {
    return new Promise((resolve) => {
      this.context.commit("setLoginLoading");

      // ใช้ข้อมูล login request ต้นฉบับ + เพิ่ม regenerate_otp = true
      const originalRequest = this.originalLoginRequest;

      const source = ActivityLogSource.LineConnect;
      API.AuthenService.DoGetAccessToken({
        userName: originalRequest.email,
        password: originalRequest.encpw,
        userType: originalRequest.userType || 0,
        source,
        regenerateOtp: true,
        recaptchaToken: originalRequest.recaptchaToken || "",
        recaptchaVersion: originalRequest.recaptchaVersion || "",
      })
        .then((response: any) => {
          // ตรวจสอบ response_code 201 ที่มี reference_otp ใหม่
          const responseCode = response.data.response_code
            ? response.data.response_code
            : AuthenticationResponseCode.OK;

          if (responseCode === AuthenticationResponseCode.RequireEmailOTP) {
            // อัพเดท reference_otp และ recoveryCodeAttempt ใหม่
            const currentTwoFactorData = this.twoFactorAuthData || {};
            const twoFactorData = {
              ...currentTwoFactorData,
              type: 1, // Email OTP
              referenceOtp: response.data.reference_otp || "",
              recoveryCodeAttempt: response.data.recovery_code_attempt !== undefined 
                ? response.data.recovery_code_attempt 
                : (currentTwoFactorData.recoveryCodeAttempt || 0),
            };
            this.context.commit("setTwoFactorAuthData", twoFactorData);

            // เก็บ reference_otp ใหม่ใน cookie
            if (response.data.reference_otp && response.data.reference_otp_live) {
              setReferenceOtpCookie(
                originalRequest.email,
                response.data.reference_otp,
                response.data.reference_otp_live
              );
            }
          } else if (responseCode === AuthenticationResponseCode.EmailOTPAlreadyRequested) {
            // EmailOTPAlreadyRequested - ตรวจสอบ cookie สำหรับ reference_otp
            let referenceOtp = response.data.reference_otp;
            if (!referenceOtp || referenceOtp === null) {
              const cookieData = getReferenceOtpCookie(originalRequest.email);
              if (cookieData && isReferenceOtpValid(originalRequest.email)) {
                referenceOtp = cookieData.referenceOtp;
              }
            }

            // อัพเดท reference_otp และ recoveryCodeAttempt จาก response
            const currentTwoFactorData = this.twoFactorAuthData || {};
            const twoFactorData = {
              ...currentTwoFactorData,
              type: 1, // Email OTP
              referenceOtp: referenceOtp || "",
              recoveryCodeAttempt: response.data.recovery_code_attempt !== undefined 
                ? response.data.recovery_code_attempt 
                : (currentTwoFactorData.recoveryCodeAttempt || 0),
            };
            this.context.commit("setTwoFactorAuthData", twoFactorData);
          }
        })
        .catch((error) => {
          console.error("Regenerate OTP Error:", error);
        });

      // Reset loading status เพื่อให้สามารถใส่ OTP ใหม่ได้
      this.context.commit("setLoginFail2FA", "");
      resolve();
    });
  }
}
